import React, { useState } from "react";
import {
  decryptByText,
  getDecryptedName,
  isAdminSubAdminLoggedIn,
  isCustomerLoggedIn,
} from "../../../../helpers/bycrypt";
import {
  CapitalizeFirstWord,
  capitalizeFirstLetter,
  formatAddress,
  formatCurrency,
  formatToLocalDate,
  momentDateTime,
  momentDDMMYY,
  formatDeliveryAddress,
  isInvoiceFeatureEnabled,
  getStaticSalespersonLabel,
  StaticSalespersonOptionLabels,
} from "../../../../helpers/utils";
import { useNavigate } from "react-router-dom";
import {
  PaymentMilestoneStatus,
  PaymentMilestoneStatusLabels,
  QuoteStatus,
  RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT,
} from "../quotationTypes";
import { svg } from "../../../../Assets/svg";
import { Button, Dropdown, Menu, message, Tooltip, Typography } from "antd";
import PayMilestoneModal from "../Components/PayMilestoneModal";
import { useAppDispatch } from "../../../../store";
import {
  deleteQuotationMilestoneAPI,
  payMilestoneInvoiceAPI,
  createMilestoneInvoiceAPI,
  unpayMilestoneAPI,
  updateDeliveryAddressForQuoteOrderAPI,
} from "../quotationApis";
import dayjs from "dayjs";
import { setQuotationDetails } from "../quotationSlice";
import ViewPaidMilestoneDetails from "../Components/ViewPaidMilestoneDetails";
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";
import { AddPaymentMilestoneModal } from "../Components/AddPaymentMilestoneModal";
import { OrderStatusEnum } from "../../Orders/orderTypes";
import { generatePaymentRecieptAPI } from "../../../Redux/orders";
import { MarkInvoiceAsPaidManuallyAPI } from "../../../Redux/invoice";
import CreateMilestoneInvoiceModal from "../Components/CreateMilestoneInvoiceModal";
import {
  getVatValue,
  VatType,
} from "../../ClientsDistributors/clientDistributorTypes";
import { ModifyDeliveryAddressModal } from "../../ClientsDistributors/Components/ModifyDeliveryAddressModal";
import { RoleTypesEnum } from "../../RolesAndPermissions/rolesAndPermissionsTypes";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../../helpers/permissionModule";

const { Link } = Typography;
const CustomerDetails = ({
  data,
  setData,
  isOrderView = false,
  paymentPermission = true,
}: {
  data: any;
  isOrderView?: boolean;
  setData?: any;
  paymentPermission?: boolean;
}) => {
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.QUOTATIONS,
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false);
  const [viewMilestoneData, setViewMilestoneData] = useState<any>(null);

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<any>(null);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] =
    useState<any>(null);
  const [isPaying, setIsPaying] = useState(false);

  const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);

  const [milestoneIdToUnpay, setMilestoneIdToUnpay] = useState<any>(null);
  const [paidDetailsIdToUnpay, setPaidDetailsIdToUnpay] = useState<any>(null);
  const [isUnpaying, setIsUnpaying] = useState(false);

  const [editingMilestone, setEditingMilestone] = useState<any>(null);
  const [paymentMilestonesOpen, setPaymentMilestonesOpen] = useState(false);
  const [isMarkAsPaidModal, setIsMarkAsPaidModal] = useState(false);
  const [markAsPaidId, setMarkAsPaidId] = useState<string | null>("");

  const [milestoneIdToDelete, setMilestoneIdToDelete] = useState<string | null>(
    null,
  );
  const [deletingMilestone, setDeletingMilestone] = useState(false);

  const [isDeliveryAddressModalOpen, setIsDeliveryAddressModalOpen] =
    useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  if (!data) return null;
  const handleDeleteConfirm = () => {
    if (!milestoneIdToDelete) return;
    setDeletingMilestone(true);
    const payload = {
      quote_id: isOrderView ? data?.obj_quote_id : data?._id,
      payment_milestone_id: milestoneIdToDelete,
    };
    dispatch(
      deleteQuotationMilestoneAPI({
        payload,
        onSuccess: (data) => {
          setData(data);
          setDeletingMilestone(false);
        },
        onError: () => {
          setDeletingMilestone(false);
        },
      }),
    );

    setDeletingMilestone(false);
    setMilestoneIdToDelete(null);
  };

  const snapshot = data.customer_snapshot || {};
  const customer = data?.customer_id || {};
  const milestones = data.payment_milestones || [];
  const preferences = snapshot.preferences || {};
  const is2BSOrder = Boolean(data?.is_2bs_order);

  const status = data?.status || QuoteStatus.DRAFT;

  const orderStatus =
    data?.order_details?.order_status || data?.order_status || undefined;

  const estimatedLeadTimeDays =
    data?.order_details?.estimated_lead_time ?? data?.estimated_lead_time;
  const estimatedLeadTimeWeeks =
    estimatedLeadTimeDays != null
      ? Number((estimatedLeadTimeDays / 7).toFixed(1))
      : null;

  const rawActualLeadTime =
    data?.order_details?.actual_lead_time ?? data?.actual_lead_time;
  const actualLeadTimeWeeks =
    rawActualLeadTime != null ? Number((rawActualLeadTime / 7).toFixed(1)) : 0;

  const isLeadTimeDispatched =
    [
      OrderStatusEnum.READY_FOR_DISPATCH,
      OrderStatusEnum.DISPATCHED,
      OrderStatusEnum.DELIVERED,
      OrderStatusEnum.UNDER_INSTALLATION,
      OrderStatusEnum.INSTALLED,
      OrderStatusEnum.CLOSED,
    ].includes(orderStatus) && rawActualLeadTime != null;

  const diffWeeks =
    estimatedLeadTimeWeeks != null
      ? Number((actualLeadTimeWeeks - estimatedLeadTimeWeeks).toFixed(1))
      : 0;
  const leadTimeExceeded = diffWeeks > 0;
  const showPaymentStatusUI =
    isOrderView ||
    [
      // QuoteStatus.APPROVED,
      // QuoteStatus.APPROVED_OVERRIDE,
      QuoteStatus.ORDER_CREATED,
    ].includes(status);

  const isMilestoneCRUDAllowed =
    paymentPermission &&
    (orderStatus === undefined ||
      orderStatus === OrderStatusEnum.ORDER_CREATED) &&
    !isCustomerLoggedIn();

  const activeMilestoneIndex = milestones.findIndex(
    (m: any) => m.paid_status !== PaymentMilestoneStatus.PAID,
  );

  const statusFindClass = (status: string) => {
    switch (status) {
      case PaymentMilestoneStatus.PENDING:
        return "statusDraft";
      case PaymentMilestoneStatus.PAID:
        return "statusApproved";
      case PaymentMilestoneStatus.OVERDUE:
        return "statusExpired";
      case PaymentMilestoneStatus.PARTIAL:
        return "statusApproved";
      default:
        return "statusDraft"; // Fallback
    }
  };

  const handleOpenCreateInvoiceModal = (milestone: any) => {
    setSelectedMilestone(milestone);
    setCreateInvoiceModalOpen(true);
  };

  const handleOpenPayInvoiceModal = (milestone: any, paidItem: any) => {
    setSelectedMilestone({
      ...milestone,
      remaining_amount: paidItem.amount_paid,
    });
    setSelectedInvoiceForPayment(paidItem);
    setPayModalOpen(true);
  };

  const handleSaveInvoice = (values: any) => {
    const payload = {
      quote_id: isOrderView ? data?.obj_quote_id : data?._id,
      payment_milestone_id: selectedMilestone._id,
      amount: values.amount,
      notes: values.notes,
      id: data?._id,
      type: isOrderView ? "order" : "quote",
    };
    setIsCreatingInvoice(true);
    dispatch(
      createMilestoneInvoiceAPI({
        payload,
        onSuccess: (response: any) => {
          setIsCreatingInvoice(false);
          const updatedMilestone = response?.milestone;
          const quotationData = response?.quote_details;

          const paidDetails = response?.milestone?.paid_details;
          const invoiceId =
            paidDetails && paidDetails.length > 0
              ? paidDetails[paidDetails.length - 1]?.invoice_id
              : undefined;
          if (invoiceId) {
            navigate(`/invoices/edit-invoice/${invoiceId}`);
          } else {
            if (isOrderView) {
              dispatch({
                type: "UPDATE_ORDER_PAYMENT_MILESTONE",
                payload: updatedMilestone,
              });
            } else {
              if (updatedMilestone && data?.payment_milestones) {
                const updatedMilestones = data.payment_milestones.map(
                  (m: any) =>
                    m._id === updatedMilestone._id ? updatedMilestone : m,
                );
                setData({
                  ...quotationData,
                  payment_milestones: updatedMilestones,
                });
              }
            }
          }
          setCreateInvoiceModalOpen(false);
          setSelectedMilestone(null);
        },
        onError: () => setIsCreatingInvoice(false),
      }),
    );
  };

  const handleSavePayment = (values: any) => {
    // 1. Prepare the Payload according to backend requirements
    const payload = {
      quote_id: isOrderView ? data?.obj_quote_id : data?._id,
      payment_milestone_id: values.milestone_id,
      invoice_id: selectedInvoiceForPayment?.invoice_id,
      amount_paid: Number(values.paymentAmount),
      payment_date: dayjs(values.paymentDate || new Date())
        .hour(dayjs().hour())
        .minute(dayjs().minute())
        .second(dayjs().second())
        ?.toISOString(),
      payment_notes: values.note,
      auto_create_order: values.createOrder,
      id: data?._id,
      type: isOrderView ? "order" : "quote",
      payment_details_id: selectedInvoiceForPayment?._id
        ? Array.isArray(selectedInvoiceForPayment._id)
          ? selectedInvoiceForPayment._id
          : [selectedInvoiceForPayment._id]
        : undefined,
    };
    setIsPaying(true);

    dispatch(
      payMilestoneInvoiceAPI({
        payload,
        onSuccess: (response: any) => {
          setIsPaying(false);
          const quotationData = response?.quote_details;
          if (isOrderView) {
            if (quotationData?.payment_milestones) {
              dispatch({
                type: "UPDATE_ORDER_PAYMENT_MILESTONES_LIST",
                payload: quotationData.payment_milestones,
              });
            }
          } else {
            if (quotationData) {
              setData(quotationData);
            }
          }

          setPayModalOpen(false);
          setSelectedMilestone(null);
          setSelectedInvoiceForPayment(null);
        },
        onError: () => {
          setIsPaying(false);
        },
      }),
    );
  };

  const handleEditFromView = () => {
    // 1. Close the View Modal
    setViewDetailsModalOpen(false);

    // 2. Set the data for the Pay Modal (using the data we were just viewing)
    setSelectedMilestone(viewMilestoneData);

    // 3. Open the Pay/Edit Modal
    setPayModalOpen(true);
  };

  const handleOpenViewDetails = (milestone: any) => {
    setViewMilestoneData(milestone);
    setViewDetailsModalOpen(true);
  };

  const handleCloseViewDetails = () => {
    setViewDetailsModalOpen(false);
    setViewMilestoneData(null);
  };

  const getActionMenu = (item: any, milestoneId: string | null = null) => {
    return (
      <Menu>
        {!item?.is_from_invoice && (
          <Menu.Item
            key="payment_details"
            onClick={() => handleOpenViewDetails(item)} // Trigger handler
          >
            Payment Details
          </Menu.Item>
        )}
        {item?.is_from_invoice && (
          <Menu.Item
            key="payment_details"
            onClick={() =>
              navigate(`/invoices/invoice-details/${item?.invoice_id}`)
            } // Trigger handler
          >
            Payment Details
          </Menu.Item>
        )}
        {(item.paid_status === PaymentMilestoneStatus.PAID ||
          item.paid_status === PaymentMilestoneStatus.PARTIAL ||
          (milestoneId && item?.status !== "unpaid")) && (
          <Menu.Item
            key="download_receipt"
            onClick={() => {
              messageApi.open({
                type: "loading",
                content: "Payment Receipt Download in progress..",
                duration: 0,
              });
              dispatch(
                generatePaymentRecieptAPI({
                  payload: {
                    milestone_id: milestoneId || item?._id,
                    payment_details_id: milestoneId ? item?._id : undefined,
                  },
                  onSuccess: () => messageApi?.destroy(),
                  onError: () => messageApi?.destroy(),
                }),
              );
            }} // Trigger handler
          >
            Download Receipt
          </Menu.Item>
        )}
        {(item?.status === "paid" || item?.status === "partially_paid") &&
          item?.payment_notes !== "Stripe payment via Invoice Webhook" && (
            <Menu.Item
              key="mark_unpaid"
              danger
              onClick={() => {
                setMilestoneIdToUnpay(milestoneId || item._id);
                setPaidDetailsIdToUnpay(milestoneId ? item._id : null);
              }}
            >
              Mark As Unpaid
            </Menu.Item>
          )}
        {item?.status === PaymentMilestoneStatus.UNPAID && (
          <Menu.Item
            key="mark_paid"
            style={{ color: "#419f20" }}
            onClick={() => {
              setMilestoneIdToUnpay(milestoneId || item._id);
              setPaidDetailsIdToUnpay(milestoneId ? item._id : null);
            }}
          >
            Mark As Paid
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const handleUnpayConfirm = () => {
    if (!milestoneIdToUnpay) return;
    setIsUnpaying(true);
    const payload = {
      quote_id: isOrderView ? data?.obj_quote_id : data?._id,
      payment_milestone_id: milestoneIdToUnpay,
      paid_details_id: paidDetailsIdToUnpay,
      id: data?._id,
      type: isOrderView ? "order" : "quote",
    };

    dispatch(
      unpayMilestoneAPI({
        payload,
        onSuccess: (response) => {
          setIsUnpaying(false);
          const updatedMilestone = response?.milestone;
          const quotationData = response?.quote_details;
          if (isOrderView) {
            dispatch({
              type: "UPDATE_ORDER_PAYMENT_MILESTONE",
              payload: updatedMilestone,
            });
          } else {
            if (updatedMilestone && data?.payment_milestones) {
              // 2. Create a new milestones array with the updated data
              const updatedMilestones = data.payment_milestones.map((m: any) =>
                m._id === updatedMilestone._id ? updatedMilestone : m,
              );

              // 3. Update the Master Object in Redux with the new array

              const updatedQuotation = {
                ...data,
                payment_milestones: updatedMilestones,
              };
              setData({
                ...quotationData,
                payment_milestones: updatedMilestones,
              });
            }
          }

          setMilestoneIdToUnpay(null);
          setPaidDetailsIdToUnpay(null);
        },
        onError: () => {
          setIsUnpaying(false);
          setMilestoneIdToUnpay(null);
          setPaidDetailsIdToUnpay(null);
        },
      }),
    );
  };

  const handleEditMilestone = (item: any) => {
    setEditingMilestone(item);
    setPaymentMilestonesOpen(true);
  };

  const handleSaveMilestone = (updatedData: any) => {
    if (isOrderView) {
      dispatch({
        type: "ADD_ORDER_PAYMENT_MILESTONE",
        payload: updatedData,
      });
    } else {
      setData(updatedData);
    }
  };

  return (
    <>
      <div className="d-flex flex-column gap-4">
        {/* 1. Basic Details */}
        <div>
          <div className="d-flex justify-content-between align-items-center mb-md-3 mb-2 flex-wrap gap-2">
            <h4 className="fs-5 fw-bold text-black mb-0">Basic Details</h4>
            {isOrderView && estimatedLeadTimeWeeks != null && (
              <div className="d-flex align-items-center gap-2">
                {!isLeadTimeDispatched ? (
                  <>
                    <span className="fs-6 fw-semibold text-secondary">
                      Estimated Lead Time:
                    </span>
                    <span className="fs-6 fw-bold text-primary">
                      {estimatedLeadTimeWeeks.toFixed(1)}{" "}
                      {estimatedLeadTimeWeeks === 1 ? "Week" : "Weeks"}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="fs-6 fw-semibold text-secondary">
                      Estimated/Actual Lead Time:
                    </span>
                    <span className="fs-6 fw-bold text-primary">
                      {estimatedLeadTimeWeeks.toFixed(1)} /{" "}
                      {actualLeadTimeWeeks.toFixed(1)} Weeks
                    </span>
                    <span
                      className={`fs-6 fw-bold ${
                        leadTimeExceeded ? "text-danger" : "text-success"
                      }`}
                    >
                      ({leadTimeExceeded ? `+${diffWeeks}` : `${diffWeeks}`}{" "}
                      {Math.abs(diffWeeks) === 1 ? "Week" : "Weeks"})
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          {contextHolder}
          <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
            <div className="row gy-md-4 gy-3">
              {/* Row 1 */}
              {!isCustomerLoggedIn() && (
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Quote For
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {snapshot?.quote_for?.name ||
                      (typeof snapshot?.quote_for === "object"
                        ? snapshot?.quote_for?.label ||
                          snapshot?.quote_for?.title
                        : undefined) ||
                      "-"}
                  </span>
                </div>
              )}
              {!isCustomerLoggedIn() && (
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-949494 d-block">
                    Customer Type
                  </label>
                  <span className="fw-semibold text-primary fs-6">
                    {snapshot.customer_type === "regular"
                      ? "Regular Customer"
                      : "New Customer"}
                  </span>
                </div>
              )}

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Company Name
                </label>
                <span className="fw-semibold text-dark fs-6">
                  {customer?.company_name || "-"}
                </span>
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Contact Person
                </label>
                <span className="fw-semibold text-dark fs-6">
                  {CapitalizeFirstWord(
                    [snapshot?.first_name, snapshot?.last_name]
                      .filter(Boolean)
                      .join(" "),
                  ) || "-"}
                </span>
              </div>

              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Phone
                </label>
                <span className="fw-semibold text-dark fs-6">
                  {snapshot.phone ? snapshot.phone : "-"}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Customer Email
                </label>
                <span className="fw-semibold text-dark fs-6 text-break">
                  {snapshot.email ? snapshot.email : "-"}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Sage Account Contact Email
                </label>
                <span className="fw-semibold text-dark fs-6 text-break">
                  {snapshot.sage_account_contact_email
                    ? decryptByText(snapshot.sage_account_contact_email)
                    : "-"}
                </span>
              </div>

              {/* Row 2 */}
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  VAT Type
                </label>
                <span className="fw-semibold text-dark fs-6">
                  {snapshot.vat_type
                    ? getVatValue(snapshot.vat_type)
                    : getVatValue(VatType.NOT_VAT_REGISTERED)}
                </span>
              </div>
              {snapshot?.vat_type === VatType.VAT_REGISTERED && (
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    VAT Number
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {snapshot.vat_number || "-"}
                  </span>
                </div>
              )}
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-949494 d-block">
                  {" "}
                  VAT Percentage
                </label>
                <span className="fw-semibold text-primary fs-6">
                  {snapshot?.tax_id?.name && !isCustomerLoggedIn() ? (
                    <>
                      {snapshot?.tax_id?.name ? snapshot?.tax_id?.name : ""}{" "}
                      {`(${
                        snapshot?.tax_id?.tax_rate !== undefined &&
                        snapshot?.tax_id?.tax_rate !== null
                          ? `${snapshot?.tax_id?.tax_rate}%`
                          : ""
                      })`}{" "}
                      {`(${
                        snapshot?.tax_id?.code
                          ? `Code : ${snapshot?.tax_id?.code}`
                          : ""
                      })`}
                    </>
                  ) : snapshot?.vat_percentage !== undefined &&
                    snapshot?.vat_percentage !== null ? (
                    `${snapshot.vat_percentage}%`
                  ) : (
                    "-"
                  )}
                </span>
              </div>

              {(!isCustomerLoggedIn() ||
                snapshot?.show_retail_price !== false) && (
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-949494 d-block">
                    Markup / Discount
                  </label>
                  <span className="fw-semibold fs-6">
                    {Number(snapshot?.margin) > 0 ? (
                      <span className="text-success d-inline-flex align-items-center gap-1">
                        <span>Markup :</span>
                        <span className="fw-bold">{snapshot?.margin}%</span>
                      </span>
                    ) : Number(snapshot?.discount) > 0 ? (
                      <span className="text-danger d-inline-flex align-items-center gap-1">
                        <span>Discount :</span>
                        <span className="fw-bold">{snapshot?.discount}%</span>
                      </span>
                    ) : (
                      <span className="text-muted">0%</span>
                    )}
                  </span>
                </div>
              )}

              {/* {(!isCustomerLoggedIn() ||
                snapshot?.show_retail_price !== false) && (
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-inline-flex align-items-center gap-1 mb-1">
                    <span>Discount</span>
                    {!isCustomerLoggedIn() &&
                      snapshot?.show_retail_price === false && (
                        <Tooltip title={RETAIL_PRICE_HIDDEN_TOOLTIP_TEXT}>
                          <span className="svg-16 text-muted cursor-pointer d-inline-flex align-items-center">
                            {svg?.restrictedAvatarIcon}
                          </span>
                        </Tooltip>
                      )}
                  </label>
                  <span className="fw-semibold text-dark fs-6 d-block">
                    {(snapshot.discount ?? "") !== ""
                      ? `${snapshot.discount}%`
                      : "-"}
                  </span>
                </div>
              )} */}
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Salesperson
                </label>
                <span className="fw-semibold text-dark fs-6">
                  {getStaticSalespersonLabel(snapshot.salesperson_id) ||
                    getStaticSalespersonLabel(snapshot.salesperson_name) ||
                    (snapshot.salesperson_name &&
                    Object.values(StaticSalespersonOptionLabels).includes(
                      snapshot.salesperson_name,
                    )
                      ? snapshot.salesperson_name
                      : snapshot.salesperson_name
                      ? snapshot.salesperson_name
                          .split(" ")
                          .map((chunk: string) => decryptByText(chunk))
                          .join(" ")
                      : "-")}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Billing Address
                </label>
                <span className="fw-semibold text-dark fs-6 text-break">
                  {formatAddress(snapshot.billing_address)}
                </span>
              </div>

              {/* Row 3 */}
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Delivery / Installation Address
                </label>
                <div className="d-flex align-items-start gap-2">
                  <span className="fw-semibold text-dark fs-6 text-break">
                    {formatDeliveryAddress(snapshot.delivery_address)}
                  </span>
                  {/* {USER_PERMISSIONS?.MODIFY && (
                    <button
                      type="button"
                      className="btn btn-link p-0 svg-32 flex-shrink-0"
                      onClick={() => setIsDeliveryAddressModalOpen(true)}
                    >
                      {svg?.editBtn}
                    </button>
                  )} */}
                </div>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Currency
                </label>
                <span className="fw-semibold text-dark fs-6">
                  {data?.currency || "GBP"}
                </span>
              </div>

              {!isOrderView && (
                <>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      Created On
                    </label>
                    <span className="fw-semibold text-dark fs-6">
                      {data.created_at
                        ? formatToLocalDate(data.created_at)
                        : "-"}
                    </span>
                  </div>
                  {data?.is_archived && (
                    <>
                      <div className="col-lg-3 col-md-4 col-sm-6">
                        <label className="fs-6 fw-normal text-secondary d-block mb-1">
                          Archived By
                        </label>
                        <span className="fw-semibold text-dark fs-6">
                          {data?.archived_by
                            ? `${getDecryptedName(data.archived_by)}${
                                data.archived_by?.role?.name
                                  ? ` (${data.archived_by.role.name})`
                                  : ""
                              }`
                            : "-"}
                        </span>
                      </div>
                      <div className="col-lg-3 col-md-4 col-sm-6">
                        <label className="fs-6 fw-normal text-secondary d-block mb-1">
                          Archived On
                        </label>
                        <span className="fw-semibold text-dark fs-6">
                          {data?.archived_at
                            ? formatToLocalDate(data.archived_at, true)
                            : "-"}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      Save/Sent On
                    </label>
                    <span className="fw-semibold text-dark fs-6">
                      {data?.validity_timer_started_at
                        ? formatToLocalDate(data.validity_timer_started_at)
                        : "-"}
                    </span>
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      Valid Until
                    </label>
                    <span className="fw-semibold text-dark fs-6">
                      {/* {data?.admin_approval_required &&
                      (data?.status === QuoteStatus.PENDING_ADMIN_APPROVAL ||
                        !data?.admin_approved_at)
                        ? "-"
                        : data.valid_till
                        ? formatToLocalDate(data.valid_till)
                        : "-"} */}
                      {data.valid_till
                        ? formatToLocalDate(data.valid_till)
                        : "-"}
                    </span>
                  </div>
                </>
              )}
              {isOrderView && (
                <>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      Order Id
                    </label>
                    <span className="fw-semibold text-dark fs-6">
                      {data?.order_id ? data.order_id : "-"}
                    </span>
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      Quote Id
                    </label>
                    <span
                      className="fw-semibold text-dark fs-6 text-decoration-underline cursor-pointer"
                      onClick={() => {
                        const targetQuoteId =
                          data?.obj_quote_id ||
                          (typeof data?.quote_id === "string" &&
                          /^[0-9a-fA-F]{24}$/.test(data?.quote_id)
                            ? data?.quote_id
                            : undefined);
                        if (targetQuoteId) {
                          navigate(
                            `/quotations/${targetQuoteId}?tab=customer-details`,
                            { state: { backPath: `/orders/${data?._id}` } },
                          );
                        }
                      }}
                    >
                      {data?.display_quote_id ||
                        (data?.quote_id &&
                        !/^[0-9a-fA-F]{24}$/.test(String(data?.quote_id))
                          ? data?.quote_id
                          : "-")}
                    </span>
                  </div>
                </>
              )}
              {!isCustomerLoggedIn() && (
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Show Retail Price
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {snapshot?.show_retail_price !== false ? "Yes" : "No"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Contact Details */}
        {snapshot?.additional_account_contact_id &&
          typeof snapshot.additional_account_contact_id === "object" && (
            <div className="row gy-3 mb-4">
              <div className="col-12">
                <h4 className="fs-5 fw-bold text-black mb-md-3 mb-2">
                  Account Contact Details
                </h4>
                <div className="card border-0 shadow-sm p-3 p-md-3 rounded-4 bg-white">
                  <div className="row gy-2">
                    <div className="col-sm-4">
                      <label className="fs-6 fw-normal text-secondary d-block mb-1">
                        Full Name
                      </label>
                      <span className="fw-semibold text-dark fs-6">
                        {CapitalizeFirstWord(
                          getDecryptedName(
                            snapshot.additional_account_contact_id,
                          ),
                        ) || "-"}
                      </span>
                    </div>
                    <div className="col-sm-4">
                      <label className="fs-6 fw-normal text-secondary d-block mb-1">
                        Phone
                      </label>
                      <span className="fw-semibold text-dark fs-6">
                        {snapshot.additional_account_contact_id.phone || "-"}
                      </span>
                    </div>
                    <div className="col-sm-4">
                      <label className="fs-6 fw-normal text-secondary d-block mb-1">
                        Email
                      </label>
                      <span className="fw-semibold text-dark fs-6 text-break">
                        {snapshot.additional_account_contact_id.email
                          ? decryptByText(
                              snapshot.additional_account_contact_id.email,
                            )
                          : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* 2. Payment Milestones */}
        {!is2BSOrder && (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-md-3 mb-2">
              <h4 className="fs-5 fw-bold text-black mb-0">Payment Milestones</h4>
              {isMilestoneCRUDAllowed && isAdminSubAdminLoggedIn() && (
                <Link
                  onClick={() => {
                    setEditingMilestone(null);
                    setPaymentMilestonesOpen(true);
                  }}
                  className="text-primary text-decoration-underline fw-bold fs-6"
                >
                  Add
                </Link>
              )}
            </div>
            <div className="card d-flex flex-column gap-3 border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
            {milestones.length > 0 ? (
              milestones.map((item: any, index: number) => {
                const isPercentage = item.value_base === "percentage";
                const currentStatus =
                  (item.paid_status as PaymentMilestoneStatus) ||
                  PaymentMilestoneStatus.PENDING;

                const isPaid = currentStatus === PaymentMilestoneStatus.PAID;
                const isParcillyPaid =
                  currentStatus === PaymentMilestoneStatus.PARTIAL;
                // Now TypeScript knows currentStatus is a valid key for the Labels record
                const statusLabel = PaymentMilestoneStatusLabels[currentStatus];

                let totalInvoiced = 0;
                if (Array.isArray(item.paid_details)) {
                  totalInvoiced = item.paid_details.reduce(
                    (sum: number, p: any) => sum + (Number(p.amount_paid) || 0),
                    0,
                  );
                }
                const calculated = Number(item.calculated_amount) || 0;
                let remainingToInvoice = Number(
                  (calculated - totalInvoiced).toFixed(2),
                );
                if (remainingToInvoice < 0) remainingToInvoice = 0;

                const showMarkAsPaid =
                  index === activeMilestoneIndex &&
                  paymentPermission &&
                  remainingToInvoice > 0;

                const colClass = showPaymentStatusUI ? "col-md-4" : "col-md-4";

                return (
                  <div
                    key={item._id || index}
                    className="border px-3 py-3 rounded-3"
                  >
                    <div className="row gy-2 align-items-md-center align-items-start">
                      <div
                        className={` ${
                          isMilestoneCRUDAllowed &&
                          !isPaid &&
                          !isParcillyPaid &&
                          !item.paid_details?.length &&
                          isAdminSubAdminLoggedIn()
                            ? "col-xl-11 col-sm-10 col-11"
                            : "col-12"
                        }`}
                      >
                        <div className="row gy-2 align-items-center">
                          {/* Column 1: Milestone Name */}
                          <div className={`${colClass} col-sm-6`}>
                            <span className="fs-6 fw-semibold text-dark">
                              {index + 1}.{" "}
                              {item?.is_other ? "Others" : item.milestone_name}
                            </span>
                          </div>

                          {/* Column 2: Payment Amount */}
                          <div className={`${colClass} col-sm-6`}>
                            <span className="fs-6 fw-medium text-dark">
                              Payment :{" "}
                              {isPercentage
                                ? `${item.value}%`
                                : formatCurrency(item.value, data?.currency)}
                              {item.calculated_amount > 0 && isPercentage && (
                                <span className="ms-1">
                                  {`(${formatCurrency(
                                    item.calculated_amount,
                                    data?.currency,
                                  )})`}
                                </span>
                              )}
                            </span>
                          </div>

                          {/* Column 4: Status & Action (Conditional) */}
                          {showPaymentStatusUI && (
                            <div
                              className={`col-lg-4 col-md-12 col-sm-6 ${
                                isMilestoneCRUDAllowed &&
                                !isPaid &&
                                !isParcillyPaid
                                  ? ""
                                  : "text-lg-end"
                              } ${
                                !showMarkAsPaid ? `text-lg-end` : `text-lg-end`
                              }`}
                            >
                              {isInvoiceFeatureEnabled && (
                                <>
                                  <span
                                    className={
                                      "quotation-status svg-10 fs-6 " +
                                      statusFindClass(currentStatus)
                                    }
                                  >
                                    {svg.dotIcon}
                                    <span className="ms-1">{statusLabel}</span>
                                  </span>
                                  {!isCustomerLoggedIn() && (
                                    <>
                                      {showMarkAsPaid && (
                                        <Button
                                          className="btn btn-sm btn-success ms-2 h-auto px-xl-4"
                                          onClick={() =>
                                            handleOpenCreateInvoiceModal(item)
                                          }
                                        >
                                          CREATE INVOICE
                                        </Button>
                                      )}
                                      {/* {currentStatus ===
                                        PaymentMilestoneStatus.PAID &&
                                        paymentPermission && (
                                          <div className="ms-2 d-inline-block">
                                            <Dropdown
                                              // ✅ Pass current item to generate specific menu
                                              overlay={getActionMenu(item)}
                                              trigger={["click"]}
                                              placement="bottomRight"
                                              className="order-md-3 order-2"
                                            >
                                              <span className="svg-32 cursor-pointer d-inline-block">
                                                {svg?.downloadBtn}
                                              </span>
                                            </Dropdown>
                                          </div>
                                        )} */}
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {isMilestoneCRUDAllowed &&
                        !isPaid &&
                        !isParcillyPaid &&
                        !item.paid_details?.length &&
                        isAdminSubAdminLoggedIn() && (
                          <div
                            className={`col-xl-1 col-sm-2 col-1 px-mob-0 mt-mob-0 d-flex flex-sm-row flex-column gap-sm-2 gap-1 justify-content-sm-end justify-content-start`}
                          >
                            <button
                              type="button"
                              className="btn btn-link p-0 svg-32"
                              onClick={() => handleEditMilestone(item)}
                            >
                              {svg?.editBtn}
                            </button>
                            <button
                              type="button"
                              className="btn btn-link p-0 svg-32 text-danger"
                              onClick={() => setMilestoneIdToDelete(item._id)}
                            >
                              {svg?.deleteBtn}
                            </button>
                          </div>
                        )}
                    </div>
                    {item?.paid_details?.length > 0 &&
                    isInvoiceFeatureEnabled ? (
                      <div>
                        <h2 className="text-dark fs-5 mt-3 mb-2 fw-bold">
                          Payment History
                        </h2>
                        {(() => {
                          const paidDetailsReversed =
                            item?.paid_details?.slice()?.reverse() || [];
                          const groupedInvoices: Record<string, any[]> = {};
                          const others: any[] = [];

                          paidDetailsReversed.forEach((paidItem: any) => {
                            if (paidItem.invoice_number) {
                              if (!groupedInvoices[paidItem.invoice_number]) {
                                groupedInvoices[paidItem.invoice_number] = [];
                              }
                              groupedInvoices[paidItem.invoice_number].push(
                                paidItem,
                              );
                            } else {
                              others.push(paidItem);
                            }
                          });

                          const renderPaidItem = (
                            paidItem: any,
                            paidIndex: any,
                            isLast: boolean = false,
                          ) => {
                            if (!paidItem?.receipt_no) return;

                            let statusClass = "";
                            let statusLabel = paidItem?.status || "";
                            if (paidItem?.status === "paid")
                              statusClass = " statusApproved";
                            if (paidItem?.status === "partially_paid")
                              statusClass = " statusApproved";
                            if (paidItem?.status === "unpaid")
                              statusClass = " statusSave";

                            if (paidItem?.status === "unpaid")
                              statusLabel = "Saved";
                            if (paidItem?.status === "paid")
                              statusLabel = "Paid";
                            if (paidItem?.status === "partially_paid")
                              statusLabel = "Paid";
                            return (
                              <div
                                key={paidItem._id || paidIndex}
                                className={`pt-1 ${isLast ? "" : "pb-2"}`}
                              >
                                <div className="row align-items-md-center align-items-start gy-2">
                                  <div
                                    className={`col-xl-3 col-md-4 col-sm-6 col-10 order-sm-1 order-1`}
                                  >
                                    <span className="fs-6 fw-semibold text-dark">
                                      {paidItem?.receipt_no
                                        ? `Receipt No : ${paidItem.receipt_no}`
                                        : `Receipt No : ${
                                            item?.is_other
                                              ? "Others"
                                              : item?.milestone_name || "-"
                                          }`}
                                    </span>
                                  </div>
                                  <div
                                    className={`col-xl-3 col-md-2 col-sm-6 col-10 order-sm-1 order-1`}
                                  >
                                    <span className="fs-6 fw-semibold text-dark">
                                      Amount :{" "}
                                      {formatCurrency(
                                        paidItem?.amount_paid,
                                        data?.currency,
                                      )}
                                    </span>
                                  </div>

                                  <div
                                    className={`col-xl-3 col-md-4 col-sm-6 order-sm-2 order-3`}
                                  >
                                    <span className="fs-6 fw-semibold text-dark">
                                      {paidItem?.status === "unpaid"
                                        ? "Created On"
                                        : "Paid On"}{" "}
                                      : {momentDateTime(paidItem?.payment_date)}
                                    </span>
                                  </div>

                                  <div
                                    className={`col-xl-3 col-md-2 col-sm-6 col-12 order-sm-3 order-4`}
                                  >
                                    <div className="gap-2 d-flex align-items-center justify-content-end justify-content-sm-start justify-content-md-end">
                                      {statusLabel && (
                                        <span
                                          className={
                                            "quotation-status svg-10 fs-6 d-flex align-items-center text-nowrap " +
                                            statusClass
                                          }
                                        >
                                          {svg.dotIcon}
                                          <span className="ms-1">
                                            {statusLabel}
                                          </span>
                                        </span>
                                      )}

                                      {paidItem?.status ===
                                        PaymentMilestoneStatus.UNPAID && (
                                        <Button
                                          className="btn btn-sm btn-success h-auto px-xl-4"
                                          onClick={() =>
                                            handleOpenPayInvoiceModal(
                                              item,
                                              paidItem,
                                            )
                                          }
                                        >
                                          MARK AS PAID
                                        </Button>
                                      )}

                                      <Dropdown
                                        overlay={getActionMenu(
                                          paidItem,
                                          item._id,
                                        )}
                                        trigger={["click"]}
                                        placement="bottomRight"
                                        className="order-md-3 order-2"
                                      >
                                        <span className="svg-32 cursor-pointer d-inline-block">
                                          {svg?.tableMoreOption}
                                        </span>
                                      </Dropdown>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          };

                          const groupedEntries =
                            Object.entries(groupedInvoices);
                          return (
                            <>
                              {groupedEntries.map(
                                (
                                  [invNo, items]: [string, any[]],
                                  groupIndex: number,
                                ) => {
                                  const invoiceTotal = items.reduce(
                                    (sum, item) =>
                                      sum + (Number(item.amount_paid) || 0),
                                    0,
                                  );
                                  const invoiceCreatedAt =
                                    items[0]?.payment_date ||
                                    items[0]?.created_at;
                                  const isLastGroup =
                                    groupIndex === groupedEntries.length - 1;
                                  const showHr =
                                    !isLastGroup || others.length > 0;

                                  const invoiceId = items[0]?.invoice_id;
                                  const isFullyPaid = items.every(
                                    (item: any) =>
                                      item.status === "paid" ||
                                      item.status === "partially_paid",
                                  );
                                  const isUnpaid = items.every(
                                    (item: any) => item.status === "unpaid",
                                  );

                                  const isReceiptGenerated = items.every(
                                    (item: any) => item.receipt_no,
                                  );

                                  const isPartiallyPaid =
                                    items?.length > 0 &&
                                    items.every(
                                      (item: any) => item.receipt_no,
                                    ) &&
                                    items.every(
                                      (item: any) =>
                                        item.status === "partially_paid" ||
                                        item.status === "paid",
                                    );

                                  const totalPaidAmount = items.reduce(
                                    (sum: number, i: any) =>
                                      sum +
                                      (i.status === "paid" ||
                                      i.status === "partially_paid"
                                        ? Number(i.amount_paid) || 0
                                        : 0),
                                    0,
                                  );
                                  const hideMarkAsPaid =
                                    isReceiptGenerated &&
                                    totalPaidAmount >= invoiceTotal;

                                  let invoiceStatusLabel = "Saved";
                                  let invoiceStatusClass =
                                    "bg-danger-10 statusSave";

                                  if (isFullyPaid || hideMarkAsPaid) {
                                    invoiceStatusLabel = "Paid";
                                    invoiceStatusClass =
                                      "text-success bg-success-10";
                                  } else if (!isUnpaid) {
                                    invoiceStatusLabel = "Partially Paid";
                                    invoiceStatusClass =
                                      "text-success bg-success-10";
                                  }

                                  const lastPaidItem =
                                    items?.[items?.length - 1];

                                  return (
                                    <div
                                      key={invNo}
                                      className={`${
                                        groupIndex > 0 ? "mt-3" : "mt-2"
                                      }`}
                                    >
                                      <div className="row align-items-md-center align-items-start gy-2 mb-1">
                                        <div className="col-xl-3 col-md-4 col-sm-6 col-10 order-sm-1 order-1">
                                          <div className="text-dark fs-5-5 fw-bold">
                                            Invoice No : {invNo}
                                          </div>
                                        </div>
                                        <div className="col-xl-3 col-md-2 col-sm-6 col-10 order-sm-1 order-1">
                                          <div className="text-dark fs-5-5 fw-bold">
                                            Amount :{" "}
                                            {formatCurrency(
                                              invoiceTotal,
                                              data?.currency,
                                            )}
                                          </div>
                                        </div>
                                        <div className="col-xl-3 col-md-4 col-sm-6 order-sm-2 order-3">
                                          <div className="text-dark fs-5-5 fw-bold">
                                            Created At :{" "}
                                            {momentDateTime(invoiceCreatedAt)}
                                          </div>
                                        </div>
                                        <div className="col-xl-3 col-md-2 col-sm-6 col-12 order-sm-3 order-4">
                                          <div className="gap-2 d-flex align-items-center justify-content-end justify-content-sm-start justify-content-md-end">
                                            <span
                                              className={
                                                "quotation-status svg-10 fs-6 d-flex align-items-center text-nowrap " +
                                                invoiceStatusClass
                                              }
                                            >
                                              {svg.dotIcon}
                                              <span className="ms-1">
                                                {invoiceStatusLabel}
                                              </span>
                                            </span>

                                            {((!hideMarkAsPaid &&
                                              invoiceId &&
                                              isReceiptGenerated &&
                                              isPartiallyPaid) ||
                                              !isReceiptGenerated) && (
                                              <Button
                                                className="btn btn-sm btn-success h-auto px-xl-4 text-uppercase"
                                                onClick={() =>
                                                  handleOpenPayInvoiceModal(
                                                    item,
                                                    lastPaidItem,
                                                  )
                                                }
                                              >
                                                MARK AS PAID
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      {items.map((paidItem, idx) =>
                                        renderPaidItem(
                                          paidItem,
                                          idx,
                                          idx === items.length - 1,
                                        ),
                                      )}
                                      {showHr && <hr />}
                                    </div>
                                  );
                                },
                              )}
                              {others.map((paidItem, idx) =>
                                renderPaidItem(
                                  paidItem,
                                  "other_" + idx,
                                  idx === others.length - 1,
                                ),
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center text-muted py-3 fst-italic">
                No payment milestones configured.
              </div>
            )}
          </div>
        </div>
        )}

        {/* 3. Preferences */}
        {/* <div>
          <h4 className="fs-5 fw-bold text-black mb-md-3 mb-2">Preferences</h4>
          <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
            <div className="row gy-md-4 gy-3">
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Color Preference
                </label>
                <span className="fw-semibold text-dark fs-6">
                  {preferences.color || "-"}
                </span>
              </div>
              <div className="col-lg-3 col-md-4 col-sm-6">
                <label className="fs-6 fw-normal text-secondary d-block mb-1">
                  Include Design Preview
                </label>
                <span className="fw-semibold text-dark fs-6">
                  {preferences.design_preview ? "Yes" : "No"}
                </span>
              </div>
            </div>
          </div>
        </div> */}

        {/* 5. Dispatch Details */}
        {/* {isOrderView && data?.dispatched_details ? (
          <div>
            <h4 className="fs-5 fw-bold text-black mb-md-3 mb-2">
              Dispatch Details
            </h4>
            <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
              <div className="row gy-md-4 gy-3">
                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Dispatch Date
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {data?.dispatched_details?.dispatched_at
                      ? momentDDMMYY(data?.dispatched_details?.dispatched_at)
                      : "-"}
                  </span>
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Tracking Number
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {data?.dispatched_details?.tracking_number || "-"}
                  </span>
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Transporter
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {data?.dispatched_details?.transporter || "-"}
                  </span>
                </div>

                <div className="col-lg-3 col-md-4 col-sm-6">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Vehicle Info
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {data?.dispatched_details?.vehicle_info || "-"}
                  </span>
                </div>

                <div className="col-12">
                  <label className="fs-6 fw-normal text-secondary d-block mb-1">
                    Dispatch Notes
                  </label>
                  <span className="fw-semibold text-dark fs-6">
                    {data?.dispatched_details?.note || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          ""
        )} */}
      </div>
      <PayMilestoneModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        onSave={handleSavePayment}
        milestoneData={selectedMilestone}
        loading={isPaying}
        showOrderCheckbox={!data?.order_id}
        currencySymbol={
          data?.currency === "USD" ? "$" : data?.currency === "EUR" ? "€" : "£"
        }
        conversionRate={data?.conversion_rate || 1}
      />

      <ViewPaidMilestoneDetails
        open={viewDetailsModalOpen}
        onClose={handleCloseViewDetails}
        milestoneData={viewMilestoneData}
        onEdit={handleEditFromView}
      />

      <CommonConfirmationModal
        open={milestoneIdToUnpay != null}
        title="Mark As Unpaid, Are You Sure?"
        subTitle="Are you sure you want to mark this payment as unpaid? This action will update the payment status and may affect related records."
        loading={isUnpaying}
        onSuccess={handleUnpayConfirm}
        onClose={() => {
          setMilestoneIdToUnpay(null);
          setPaidDetailsIdToUnpay(null);
        }}
        confirmButtonText="Mark As Unpaid"
      />

      <CommonConfirmationModal
        open={isMarkAsPaidModal}
        title="Mark Invoice As Paid"
        subTitle="Are you sure you want to mark this invoice as paid?"
        loading={false}
        onSuccess={() => {
          if (markAsPaidId) {
            dispatch(
              MarkInvoiceAsPaidManuallyAPI(
                markAsPaidId,
                {},
                (response: any) => {
                  setIsMarkAsPaidModal(false);
                  setMarkAsPaidId("");

                  // Update milestone data
                  if (isOrderView) {
                    if (response?.order_id?.payment_milestones) {
                      dispatch({
                        type: "UPDATE_ORDER_PAYMENT_MILESTONES_LIST",
                        payload: response.order_id.payment_milestones,
                      });
                    }
                  } else {
                    if (response?.quote_id?.payment_milestones) {
                      const updatedMilestones =
                        response.quote_id.payment_milestones;
                      setData({
                        ...data,
                        payment_milestones: updatedMilestones,
                      });
                    }
                  }
                },
              ),
            );
          }
        }}
        onClose={() => {
          setIsMarkAsPaidModal(false);
          setMarkAsPaidId("");
        }}
        confirmButtonText="Confirm"
      />

      {isDeliveryAddressModalOpen && (
        <ModifyDeliveryAddressModal
          open={isDeliveryAddressModalOpen}
          onClose={() => setIsDeliveryAddressModalOpen(false)}
          addressData={snapshot?.delivery_address}
          isDistributor={snapshot?.quote_for?.key === RoleTypesEnum.DISTRIBUTOR}
          quoteData={data}
          currency={data?.currency || data?.quote_id?.currency}
          conversion_rate={
            data?.conversion_rate || data?.quote_id?.conversion_rate
          }
          onSubmitOverride={(payload, setSaving) => {
            setSaving(true);
            if (isOrderView) {
              payload.order_id = data._id;
              dispatch(
                updateDeliveryAddressForQuoteOrderAPI({
                  payload,
                  onSuccess: (response) => {
                    const newData = {
                      ...data,
                      customer_snapshot: response?.customer_snapshot,
                    };
                    dispatch({
                      type: "GET_ORDERS_DETAILS",
                      payload: newData,
                    });
                    setSaving(false);
                    setIsDeliveryAddressModalOpen(false);
                  },
                  onError: () => {
                    setSaving(false);
                  },
                }),
              );
            } else {
              payload.quote_id = data._id;
              dispatch(
                updateDeliveryAddressForQuoteOrderAPI({
                  payload,
                  onSuccess: (data) => {
                    setData(data);
                    setSaving(false);
                    setIsDeliveryAddressModalOpen(false);
                  },
                  onError: () => {
                    setSaving(false);
                  },
                }),
              );
            }
            setSaving(false);
            // setIsDeliveryAddressModalOpen(false);
          }}
        />
      )}

      <CreateMilestoneInvoiceModal
        open={createInvoiceModalOpen}
        onClose={() => setCreateInvoiceModalOpen(false)}
        onSave={handleSaveInvoice}
        milestoneData={selectedMilestone}
        loading={isCreatingInvoice}
        currencySymbol={
          data?.currency === "USD" ? "$" : data?.currency === "EUR" ? "€" : "£"
        }
        conversionRate={data?.conversion_rate || 1}
      />

      <AddPaymentMilestoneModal
        open={paymentMilestonesOpen}
        onClose={() => setPaymentMilestonesOpen(false)}
        onSave={handleSaveMilestone}
        milestoneData={editingMilestone}
        isInstantAddition={true}
        quotationId={isOrderView ? data?.obj_quote_id : data?._id}
        existingMilestones={milestones}
        currencySymbol={
          data?.currency === "USD" ? "$" : data?.currency === "EUR" ? "€" : "£"
        }
      />

      <CommonConfirmationModal
        open={milestoneIdToDelete != null}
        title="Remove Payment Milestones, Are You Sure?"
        subTitle="Removing payment milestones will delete all related milestone details from this project. Please confirm before proceeding."
        loading={deletingMilestone}
        onSuccess={handleDeleteConfirm}
        onClose={() => setMilestoneIdToDelete(null)}
      />
    </>
  );
};

export default CustomerDetails;
